import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CourseData, GradeOptions } from "@packages/constant/CourseData";
import {
  ClassroomResponseDto,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import { useCreateClassroom } from "@packages/api/madlenAPI";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { addClassroom, getCurrentTeacher } from "@packages/store/userSlice";
import { useToast } from "@packages/contexts/ToastContext";
import Modal from "@core/modal";
import { Button } from "@core/button";
import Select from "react-select";

interface SelectOption {
  value: string;
  label: string;
}

interface CreateClassroomModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (newClassroom: ClassroomResponseDto) => void;
  shouldUseModal?: boolean;
}

export const CreateClassroomModal = ({
  show,
  onClose,
  onSubmit,
  shouldUseModal = true,
}: CreateClassroomModalProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const [name, setName] = useState("");
  const [gradeOption, setGradeOption] = useState<SelectOption>({
    value: "8",
    label: "8",
  });
  const [courseTypeOption, setCourseTypeOption] = useState<SelectOption>({
    value: i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN",
    label: t(
      "courseType." +
        (i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN"),
    ),
  });

  const teacherId = useAppSelector((state) => state.user.teacher?.id);

  const { mutateAsync: createClassroom } = useCreateClassroom();
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  // Course options based on selected grade
  const courseOptions: SelectOption[] = Object.entries(CourseData)
    .filter(([_, value]) => value.visible(Number(gradeOption.value)))
    .map(([key, value]) => ({
      value: key,
      label: t("courseType." + value.name(Number(gradeOption.value))),
    }));

  const handleGradeChange = (selectedGrade: SelectOption | null) => {
    if (selectedGrade) {
      setGradeOption(selectedGrade);

      // Update course type based on new grade
      const availableCourses = Object.entries(CourseData).filter(([_, value]) =>
        value.visible(Number(selectedGrade.value)),
      );
      const firstAvailableCourse =
        availableCourses[0]?.[0] as GetTopicsCourseType;

      if (firstAvailableCourse) {
        const courseValue = availableCourses[0];
        setCourseTypeOption({
          value: firstAvailableCourse,
          label: t(
            "courseType." + courseValue[1].name(Number(selectedGrade.value)),
          ),
        });
      }
    }
  };

  const handleCourseTypeChange = (selectedCourse: SelectOption | null) => {
    if (selectedCourse) {
      setCourseTypeOption(selectedCourse);
    }
  };

  const handleSubmit = async () => {
    if (name.trim() === "") {
      addToast({
        variant: "error",
        title: t("students.actions.nameRequired"),
        description: t("students.actions.nameRequiredDesc"),
        duration: 3000,
      });
      return;
    }
    try {
      setLoading(true);
      const newClassroom = await createClassroom({
        data: {
          name,
          grade: Number(gradeOption.value),
          courseType: courseTypeOption.value as GetTopicsCourseType,
          teacherId: teacherId!,
        },
      });
      dispatch(addClassroom(newClassroom));
      dispatch(getCurrentTeacher());
      setLoading(false);
      onSubmit(newClassroom);

      // Reset form
      setName("");
      setGradeOption({ value: "8", label: "8" });
      const defaultCourseType =
        i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN";
      setCourseTypeOption({
        value: defaultCourseType,
        label: defaultCourseType,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Dark mode compatible react-select styles
  const selectStyles = {
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: document.documentElement.classList.contains("dark")
        ? "rgb(55 65 81)" // dark:bg-gray-700
        : "white",
      borderColor: state.isFocused
        ? document.documentElement.classList.contains("dark")
          ? "rgb(96 165 250)" // dark:focus:border-blue-400
          : "rgb(59 130 246)" // focus:border-blue-500
        : document.documentElement.classList.contains("dark")
          ? "rgb(75 85 99)" // dark:border-gray-600
          : "rgb(209 213 219)", // border-gray-300
      color: document.documentElement.classList.contains("dark")
        ? "rgb(249 250 251)" // dark:text-gray-100
        : "rgb(17 24 39)", // text-gray-900
      boxShadow: state.isFocused
        ? document.documentElement.classList.contains("dark")
          ? "0 0 0 2px rgba(96, 165, 250, 0.2)" // dark:focus:ring-blue-400
          : "0 0 0 2px rgba(59, 130, 246, 0.2)" // focus:ring-blue-500
        : "none",
      minHeight: "42px",
      "&:hover": {
        borderColor: document.documentElement.classList.contains("dark")
          ? "rgb(96 165 250)" // dark:hover:border-blue-400
          : "rgb(59 130 246)", // hover:border-blue-500
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: document.documentElement.classList.contains("dark")
        ? "rgb(249 250 251)" // dark:text-gray-100
        : "rgb(17 24 39)", // text-gray-900
    }),
    input: (base: any) => ({
      ...base,
      color: document.documentElement.classList.contains("dark")
        ? "rgb(249 250 251)" // dark:text-gray-100
        : "rgb(17 24 39)", // text-gray-900
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "rgb(156 163 175)", // text-gray-400 (both modes)
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: document.documentElement.classList.contains("dark")
        ? "rgb(55 65 81)" // dark:bg-gray-700
        : "white",
      border: document.documentElement.classList.contains("dark")
        ? "1px solid rgb(75 85 99)" // dark:border-gray-600
        : "1px solid rgb(209 213 219)", // border-gray-300
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? document.documentElement.classList.contains("dark")
          ? "rgba(59, 130, 246, 0.2)" // dark:bg-blue-500/20
          : "rgb(239 246 255)" // bg-blue-50
        : state.isFocused
          ? document.documentElement.classList.contains("dark")
            ? "rgb(75 85 99)" // dark:bg-gray-600
            : "rgb(243 244 246)" // bg-gray-100
          : "transparent",
      color: state.isSelected
        ? document.documentElement.classList.contains("dark")
          ? "rgb(96 165 250)" // dark:text-blue-400
          : "rgb(37 99 235)" // text-blue-600
        : document.documentElement.classList.contains("dark")
          ? "rgb(209 213 219)" // dark:text-gray-300
          : "rgb(55 65 81)", // text-gray-700
      "&:hover": {
        backgroundColor: state.isSelected
          ? document.documentElement.classList.contains("dark")
            ? "rgba(59, 130, 246, 0.2)" // dark:bg-blue-500/20
            : "rgb(239 246 255)" // bg-blue-50
          : document.documentElement.classList.contains("dark")
            ? "rgb(75 85 99)" // dark:bg-gray-600
            : "rgb(243 244 246)", // bg-gray-100
      },
    }),
    noOptionsMessage: (base: any) => ({
      ...base,
      color: "rgb(156 163 175)", // text-gray-400 (both modes)
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      backgroundColor: document.documentElement.classList.contains("dark")
        ? "rgb(75 85 99)" // dark:bg-gray-600
        : "rgb(209 213 219)", // bg-gray-300
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "rgb(156 163 175)", // text-gray-400
      "&:hover": {
        color: document.documentElement.classList.contains("dark")
          ? "rgb(209 213 219)" // dark:hover:text-gray-300
          : "rgb(107 114 128)", // hover:text-gray-500
      },
    }),
  };

  const content = (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("students.table.name")}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
          placeholder={t("students.table.enterName")}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t("students.table.grade")}
        </label>
        <Select
          options={GradeOptions(t)}
          value={gradeOption}
          onChange={handleGradeChange}
          placeholder={t("students.table.selectGrade")}
          noOptionsMessage={() => t("students.create.noOptions")}
          menuPortalTarget={document.body}
          styles={selectStyles}
          menuPosition="absolute"
          menuPlacement="auto"
          maxMenuHeight={200}
          menuShouldScrollIntoView={false}
          isSearchable
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t("assignments.table.course")}
        </label>
        <Select
          options={courseOptions}
          value={courseTypeOption}
          onChange={handleCourseTypeChange}
          placeholder={t("assignments.table.selectCourse")}
          noOptionsMessage={() => t("students.create.noOptions")}
          menuPortalTarget={document.body}
          styles={selectStyles}
          menuPosition="absolute"
          menuPlacement="auto"
          maxMenuHeight={200}
          menuShouldScrollIntoView={false}
          isSearchable
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>
    </div>
  );

  const actions = [
    {
      label: t("common.cancel"),
      onClick: onClose,
      variant: "secondary",
    },
    {
      label: t("common.continue"),
      onClick: handleSubmit,
      variant: "primary",
      loading,
    },
  ];

  if (!shouldUseModal) {
    if (!show) {
      return null;
    }
    return (
      <div className="space-y-6">
        {content}

        {actions && actions.length > 0 && (
          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 justify-end">
            {actions.map((action, index) => (
              <Button
                key={index}
                onClick={action.onClick}
                rounded="md"
                size="md"
                disabled={action.loading}
                loading={action.loading}
                textColor={
                  action.variant === "primary"
                    ? "text-white"
                    : "text-gray-800 dark:text-gray-100"
                }
                bgColor={
                  action.variant === "primary"
                    ? "bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                    : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                }
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal
      id="create-classroom-modal"
      show={show}
      onClose={onClose}
      title={t("students.actions.createClassroom")}
      actions={actions as any}
    >
      {content}
    </Modal>
  );
};
